import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { IntegrationService } from '../../../../../services/integration.service';
import { ToastrService } from 'ngx-toastr';
import { FNFResponse } from '../../../../../models/response';
import { Subject, takeUntil } from 'rxjs';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
@Component({
  selector: 'app-clover-integration',
  standalone: true,
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    ReactiveFormsModule,
    MatIcon,
    CommonModule,
    NgxSpinnerModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
  ],
  templateUrl: './clover-integration.component.html',
  styleUrl: './clover-integration.component.css',
})
export class CloverIntegrationComponent implements OnInit, OnDestroy {
  integrationService = inject(IntegrationService);
  ngxSpinnerService = inject(NgxSpinnerService);
  toastr = inject(ToastrService);
  readonly dialogRef = inject(MatDialogRef<CloverIntegrationComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  modules: any = this.initialModules();

  cloverConnectStatus: string = '';
  connectionLoading: boolean = false;
  confirmConnect: boolean = true;
  continueConnect: boolean = false;
  successConnect: boolean = false;
  errorConnect: boolean = false;
  errorAuthorization: boolean = false;
  retryLoading: boolean = false;

  private destroy$ = new Subject<void>();
  // takeUntil(destroy$) only cuts the request in flight; a poll queued with
  // setTimeout would still re-subscribe after close (destroy$ is completed by
  // then and never fires again), so the timer and this flag stop the loop.
  private destroyed = false;
  private pollTimer?: ReturnType<typeof setTimeout>;

  ngOnInit() {
    if (this.data?.cloverAuthDetails?.status) {
      this.cloverConnectStatus = this.data.cloverAuthDetails.status;
    }
  }

  ngOnDestroy() {
    this.destroyed = true;
    clearTimeout(this.pollTimer);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initialModules() {
    return [
      { name: 'Basic details', iconCheck: false, iconLoading: true, iconDot: false },
      { name: 'Types & Categories', iconCheck: false, iconLoading: false, iconDot: true },
      { name: 'Customizations', iconCheck: false, iconLoading: false, iconDot: true },
      { name: 'Menu items', iconCheck: false, iconLoading: false, iconDot: true },
      { name: 'Finishing setup', iconCheck: false, iconLoading: false, iconDot: true },
    ];
  }

  /**
   * Retry must start a brand-new OAuth attempt. Reopening the old auth_url
   * leaves the backend row on AUTHORIZATION_FAILED / FAILED, so the first
   * status poll reads that stale result and fails before the merchant has
   * finished in the Clover tab. GET /clover/auth issues a fresh state and
   * resets the row to INITIATED; we then return to the confirm step so the
   * Clover tab opens from a real click (not blocked as a popup).
   */
  retryConnect() {
    this.retryLoading = true;
    this.integrationService
      .getCloverAuthorization()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FNFResponse<any>) => {
          this.retryLoading = false;
          if (!response.data && response.error) {
            this.toastr.error(response.error.error?.message || response.error.message, 'Error');
            return;
          } else if (!response.data) {
            this.toastr.error('No data found!', 'Error');
            return;
          }
          this.data.cloverAuthDetails = response.data;
          this.cloverConnectStatus = response.data.status || '';
          this.modules = this.initialModules();
          this.confirmConnect = true;
          this.connectionLoading = false;
          this.continueConnect = false;
          this.errorConnect = false;
          this.successConnect = false;
          this.errorAuthorization = false;
        },
        error: () => {
          this.retryLoading = false;
          this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        },
      });
  }

  connectClover() {
    if (this.data?.cloverAuthDetails?.auth_url) {
      window.open(this.data.cloverAuthDetails.auth_url, '_blank');
      this.confirmConnect = false;
      this.connectionLoading = true;
      this.errorConnect = false;
      this.successConnect = false;
      this.errorAuthorization = false;
      this.cloverConnectStatus = 'CONNECTING';
      this.ngxSpinnerService.show('cloverSpinner');
      this.getCloverConnectionStatus();
    }
  }

  getCloverConnectionStatus() {
    if (this.destroyed) {
      return;
    }
    this.integrationService
      .getCloverConnectionStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.toastr.error(response.error.error?.message || response.error.message, 'Error');
            return;
          } else if (!response.data) {
            this.toastr.error('No data found!', 'Error');
            return;
          }
          this.cloverConnectStatus = response.data.status;
          if (response.data.status === 'AUTHORIZED') {
            this.pollTimer = setTimeout(() => {
              this.continueCloverMigration();
            }, 1000);
          } else if (response.data.status === 'AUTHORIZATION_FAILED') {
            this.displayAuthorizationError();
          } else if (response.data.status === 'COMPLETED') {
            this.modules[4].iconCheck = true;
            this.modules[4].iconLoading = false;
            this.modules[4].iconDot = false;
            this.displayConnectionSuccess();
          } else if (response.data.status === 'FAILED') {
            this.displayConnectionError();
          } else {
            this.pollTimer = setTimeout(() => {
              this.getCloverConnectionStatus();
            }, 3000);
          }
        },
        error: () => {
          this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        },
      });
  }

  continueCloverMigration() {
    if (this.destroyed) {
      return;
    }
    this.confirmConnect = false;
    this.connectionLoading = false;
    this.continueConnect = true;
    this.errorConnect = false;
    this.successConnect = false;
    this.errorAuthorization = false;

    setTimeout(() => {
      this.modules[0].iconCheck = true;
      this.modules[0].iconLoading = false;
      this.modules[1].iconLoading = true;
      this.modules[1].iconDot = false;
    }, 5000);
    setTimeout(() => {
      this.modules[1].iconCheck = true;
      this.modules[1].iconLoading = false;
      this.modules[2].iconLoading = true;
      this.modules[2].iconDot = false;
    }, 10000);
    setTimeout(() => {
      this.modules[2].iconCheck = true;
      this.modules[2].iconLoading = false;
      this.modules[3].iconLoading = true;
      this.modules[3].iconDot = false;
    }, 15000);
    setTimeout(() => {
      this.modules[3].iconCheck = true;
      this.modules[3].iconLoading = false;
      this.modules[4].iconLoading = true;
      this.modules[4].iconDot = false;
    }, 20000);
    this.cloverMigration();
  }

  cloverMigration() {
    this.integrationService
      .cloverMigration({})
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FNFResponse<any>) => {
          if (!response.data && response.error) {
            this.displayConnectionError();
            return;
          } else if (!response.data) {
            this.displayConnectionError();
            return;
          }
          this.getCloverConnectionStatus();
        },
        error: () => {
          this.displayConnectionError();
        },
      });
  }

  displayConnectionSuccess() {
    this.errorAuthorization = false;
    this.confirmConnect = false;
    this.connectionLoading = false;
    this.continueConnect = false;
    this.errorConnect = false;
    this.successConnect = true;
  }

  displayConnectionError() {
    this.errorAuthorization = false;
    this.confirmConnect = false;
    this.connectionLoading = false;
    this.continueConnect = false;
    this.errorConnect = true;
    this.successConnect = false;
  }

  displayAuthorizationError() {
    this.errorAuthorization = true;
    this.confirmConnect = false;
    this.connectionLoading = false;
    this.continueConnect = false;
    this.errorConnect = false;
    this.successConnect = false;
  }

  finishSetup() {
    this.dialogRef.close('finishSetup');
  }
}
